"use client";

import {
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownTrigger,
} from "@heroui/react";
import { Icon } from "@iconify/react";
import { useEffect, useMemo, useRef } from "react";
import { useTranslations } from "next-intl";
import { LuChevronDown, LuTrophy } from "react-icons/lu";

import { UnifiedChatBell } from "@/components/chat/UnifiedChatBell";
import { useAuth } from "@/contexts/AuthContext";
import { useChatInbox } from "@/hooks/useChatInbox";
import { useNavigation } from "@/hooks/useNavigation";
import { useTournamentPoolEntries } from "@/hooks/usePool";
import { useUserTeamForTournament } from "@/hooks/useUserTeamForTournament";
import { useUserTeams } from "@/hooks/useUserTeams";
import { useUserTournaments } from "@/hooks/useUserTournaments";
import { Link, isActivePath, usePathname } from "@/i18n/routing";
import {
  ARCHIVED_VISIBLE_SECTION_IDS,
  MAIN_NAV_ITEMS,
  TOURNAMENT_NAV_SECTIONS,
  buildSectionHref,
} from "@/lib/navigation-config";
import { isUserAdmin } from "@/lib/users";
import type { MobileNavProps, TournamentSectionId } from "@/types/navigation";

import type { TeamNavItem, TournamentNavItem } from "../PublicHeader";
import { TowerChallengerLogo } from "../TowerChallengerLogo";
import { UserMenu } from "../UserMenu";

/**
 * Two-row desktop header for tournament pages.
 *
 * Row 1 (56px): brand + app-level nav (home/tournaments/how-to/start) +
 *   actions (tower link, chat bell, user menu).
 * Row 2 (44px): tournament name + sub-tabs (Tower/Matches/Chat/...). My-team
 *   and Pool tabs are membership-gated, matching the legacy TournamentNavbar.
 *
 * Match chats live in the desktop ChatSidebar (master-detail), not the
 * sub-tab row — see ChatSidebar / ChatLayout. The mobile equivalent is
 * MobileNav.
 */
export function DesktopNav({
  tournamentName,
  tournamentSlug,
  tournamentId,
  archived,
}: MobileNavProps) {
  const t = useTranslations();
  const pathname = usePathname();
  const nav = useNavigation();
  const { user, loading: userLoading } = useAuth();

  const { data: userTeam, isLoading: isLoadingTeam } =
    useUserTeamForTournament(tournamentId);
  const hasTeam = !!userTeam;
  const { data: poolEntries } = useTournamentPoolEntries(tournamentId);
  const poolCount = poolEntries?.length ?? 0;

  const { groups } = useChatInbox(user?.id);
  const { data: userTournaments = [] } = useUserTournaments(
    user?.tournamentIds,
  );
  const { data: userTeams = [] } = useUserTeams();

  const tournamentNavItems = useMemo<TournamentNavItem[]>(
    () =>
      userTournaments.map((tournament) => ({
        [tournament.id]: { name: tournament.name, slug: tournament.slug },
      })),
    [userTournaments],
  );
  const teamNavItems = useMemo<TeamNavItem[]>(
    () =>
      userTeams.map((team) => ({
        teamId: team.teamId,
        teamName: team.teamName,
        tournamentSlug: team.tournamentSlug,
        tournamentName: team.tournamentName,
      })),
    [userTeams],
  );

  const otherTournaments = useMemo(
    () =>
      tournamentNavItems
        .map((item) => {
          const id = Object.keys(item)[0];
          return { id, ...item[id] };
        })
        .filter((t) => t.slug !== tournamentSlug),
    [tournamentNavItems, tournamentSlug],
  );
  const hasOtherTournaments = otherTournaments.length > 0;

  const chatUnread = useMemo(
    () =>
      [...groups.tournament, ...groups.team, ...groups.match]
        .filter((e) => e.tournamentId === tournamentId)
        .reduce((sum, e) => sum + (e.unreadCount ?? 0), 0),
    [groups, tournamentId],
  );

  // Sub-tab visibility mirrors the legacy TournamentNavbar gating.
  const visibleSections = useMemo(() => {
    return TOURNAMENT_NAV_SECTIONS.filter((section) => {
      // Archived tournaments are read-only: only tower, chat and statistics.
      if (archived) return ARCHIVED_VISIBLE_SECTION_IDS.includes(section.id);
      if (section.id === "my-team") {
        if (user && isLoadingTeam) return true;
        return hasTeam;
      }
      if (section.id === "pool") {
        return !!user;
      }
      return true;
    });
  }, [archived, user, isLoadingTeam, hasTeam]);

  // Publish height so the page can leave room beneath this fixed header.
  const headerRef = useRef<HTMLElement>(null);
  useEffect(() => {
    const el = headerRef.current;
    if (!el) return;
    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) return;
      document.documentElement.style.setProperty(
        "--desktop-nav-h",
        `${entry.contentRect.height}px`,
      );
    });
    observer.observe(el);
    return () => {
      observer.disconnect();
      document.documentElement.style.removeProperty("--desktop-nav-h");
    };
  }, []);

  return (
    <header
      ref={headerRef}
      className="fixed inset-x-0 top-0 z-50 hidden bg-tn-bg-nav lg:block"
    >
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-x-0 top-0 h-px"
        style={{
          background:
            "linear-gradient(90deg, transparent, rgba(250,204,21,0.5), transparent)",
        }}
      />

      {/* Row 1 */}
      <div className="mx-auto flex h-14 max-w-[1600px] items-center justify-between gap-6 px-6">
        <Link href="/" className="flex items-center">
          <TowerChallengerLogo size="sm" variant="full" />
        </Link>

        <nav
          aria-label={t("nav.aria.openMainMenu")}
          className="flex flex-1 items-center justify-center gap-1"
        >
          {MAIN_NAV_ITEMS.map((item) => {
            const isHome = item.href === "/";
            const active = isActivePath(pathname, item.href, isHome);
            return (
              <Link
                key={item.id}
                href={item.href}
                aria-current={active ? "page" : undefined}
                className={
                  "relative rounded-md px-3.5 py-2 font-display text-[12px] font-semibold uppercase tracking-[0.12em] transition-colors " +
                  (active
                    ? "text-tn-gold after:absolute after:inset-x-3.5 after:bottom-0.5 after:h-px after:bg-gradient-to-r after:from-transparent after:via-tn-gold after:to-transparent"
                    : "text-tn-text-secondary hover:bg-tn-elevated hover:text-tn-text-primary")
                }
              >
                {t(item.labelKey)}
              </Link>
            );
          })}
        </nav>

        <div className="flex items-center gap-1.5">
          {userLoading ? (
            <div
              role="status"
              aria-label={t("common.loading")}
              className="flex items-center gap-1.5"
            >
              <div className="size-9 rounded-md border border-tn-border bg-tn-elevated/50 animate-pulse" />
              <div className="size-9 rounded-md border border-tn-border bg-tn-elevated/50 animate-pulse" />
              <div className="h-9 w-[110px] rounded-md border border-tn-border bg-tn-elevated/50 animate-pulse" />
            </div>
          ) : user ? (
            <>
              <Link
                href={`/tournaments/${tournamentSlug}`}
                aria-label={t("header.nav.myHome")}
                className="flex size-9 items-center justify-center rounded-md border border-tn-border bg-transparent text-tn-text-secondary transition-colors hover:border-tn-gold/50 hover:bg-tn-elevated hover:text-tn-gold"
              >
                <Icon icon="game-icons:stone-tower" className="text-xl" />
              </Link>
              <UnifiedChatBell />
              <UserMenu
                userName={user.name}
                tournamentNavItems={tournamentNavItems}
                teamNavItems={teamNavItems}
                isOrganizer={isUserAdmin(user)}
              />
            </>
          ) : (
            <Link
              href="/login"
              className="rounded-md border border-tn-gold/40 bg-tn-gold/10 px-3 py-1.5 font-display text-[12px] font-semibold uppercase tracking-[0.12em] text-tn-gold transition-colors hover:bg-tn-gold/15"
            >
              {t("auth.login.signIn")}
            </Link>
          )}
        </div>
      </div>

      {/* Hairline between rows */}
      <div className="mx-auto h-px max-w-[1600px] bg-tn-border" />

      {/* Row 2 */}
      <div className="mx-auto flex h-11 max-w-[1600px] items-center gap-5 px-6">
        <div className="flex h-6 shrink-0 items-center border-r border-tn-border pr-5">
          {hasOtherTournaments ? (
            <Dropdown
              placement="bottom-start"
              shouldBlockScroll={false}
              portalContainer={
                typeof window !== "undefined" ? document.body : undefined
              }
            >
              <DropdownTrigger>
                <button
                  type="button"
                  aria-label={t("globalFeed.selectTournament")}
                  className="-mx-1.5 flex h-6 items-center gap-2 rounded-md px-1.5 transition-colors hover:bg-tn-elevated focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tn-gold/60"
                >
                  <LuTrophy size={14} className="text-tn-gold/70" />
                  <span className="whitespace-nowrap text-[13px] font-semibold text-tn-text-primary">
                    {tournamentName}
                  </span>
                  <LuChevronDown
                    size={12}
                    className="text-tn-text-muted"
                    strokeWidth={2.5}
                  />
                </button>
              </DropdownTrigger>
              <DropdownMenu
                aria-label={t("globalFeed.selectTournament")}
                className="w-[260px]"
              >
                {otherTournaments.map((item) => (
                  <DropdownItem
                    key={item.id}
                    as={Link}
                    href={`/tournaments/${item.slug}`}
                    startContent={
                      <Icon icon="game-icons:stone-tower" width={18} />
                    }
                  >
                    {item.name}
                  </DropdownItem>
                ))}
              </DropdownMenu>
            </Dropdown>
          ) : (
            <div className="flex items-center gap-2">
              <LuTrophy size={14} className="text-tn-gold/70" />
              <span className="whitespace-nowrap text-[13px] font-semibold text-tn-text-primary">
                {tournamentName}
              </span>
            </div>
          )}
        </div>

        <div className="flex flex-1 items-center gap-0.5">
          {visibleSections.map((section) => {
            const href = buildSectionHref(
              section.hrefTemplate,
              tournamentSlug,
            );
            const active =
              nav.activeL1 === (section.id as TournamentSectionId);
            const unread = section.id === "chat" ? chatUnread : 0;
            const badge =
              section.id === "pool" && poolCount > 0 ? poolCount : unread;
            return (
              <Link
                key={section.id}
                href={href}
                aria-current={active ? "page" : undefined}
                className={
                  "relative flex items-center gap-1.5 rounded-md px-3 py-1.5 font-display text-[11px] font-semibold uppercase tracking-[0.14em] transition-colors " +
                  (active
                    ? "bg-tn-gold/[0.06] text-tn-gold after:absolute after:inset-x-3 after:-bottom-[7px] after:h-0.5 after:rounded-t-sm after:bg-tn-gold after:shadow-[0_0_8px_rgba(250,204,21,0.35)]"
                    : "text-tn-text-secondary hover:bg-tn-elevated hover:text-tn-text-primary")
                }
              >
                {t(section.labelKey)}
                {badge > 0 && (
                  <span
                    className={
                      "inline-flex h-4 min-w-4 items-center justify-center rounded-full px-1.5 text-[10px] font-bold leading-none text-white " +
                      (section.id === "pool"
                        ? "bg-tn-purple"
                        : "bg-tn-purple")
                    }
                  >
                    {badge}
                  </span>
                )}
              </Link>
            );
          })}
        </div>
      </div>
    </header>
  );
}
